import React, { FunctionComponent, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAppMainState } from '@modules/layout/layout/components/AppMainStateProvider';
import { LayoutType } from '@modules/layout/layout/components/AppLayout';
import HeadingItem from '@modules/utilities/interfaces/HeadingItem';
import {
  getMemberSlug,
  isInheritIteratorKey,
} from '@modules/apiReference/page/utils/apiReferenceUtils';

import array from '@modules/ui/shared/utils/array';
import {
  treeDelimiter,
  traverseTree,
  getNodeIdByFullPath,
  getParentNodeId,
  getNodeIdsToExpand,
  getVisibleTreeNodes,
} from '@modules/ui/shared/utils/tree';
import TreeNode from '@modules/ui/shared/interfaces/TreeNode';
import { getTreeItemElementId } from '@modules/ui/shared/components/Tree';

import { useMediaQuery } from '@rbx/ui';
import { appMain, rightNavMinBreakpoint } from '@modules/layout/layout/constants/layoutConstants';
import { throwErrorOnServer } from '@modules/utilities/utils/log';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useElementsShowInView from '../hooks/useElementsShowInView';
import AppMainConfigContext, { ExpandableInfo, TreeNodeWithHeading } from '../AppMainConfigContext';
import { showElementInViewById } from '../utils/dom';

interface AppMainConfigProviderProps {
  layoutType: LayoutType;
  headings?: HeadingItem[];
  pageTitle: string;
}

const rootNodeId = 'root';

/**
 * try to keep the reference as same as possible, used in setState
 */
export function addOrRemoveStateElement(
  prevElementIds: string[],
  elementId: string,
  adding: boolean,
): string[] {
  if (adding) {
    if (prevElementIds.includes(elementId)) {
      return prevElementIds;
    }
    return [...prevElementIds, elementId];
  }
  if (prevElementIds.includes(elementId)) {
    return prevElementIds.filter((id) => id !== elementId);
  }
  return prevElementIds;
}

const setObervableElementIds = (treeNode: TreeNode, expandedIds: string[]) =>
  getVisibleTreeNodes(treeNode, expandedIds)
    .filter((nodeId) => nodeId !== rootNodeId)
    .map(getNodeIdByFullPath);

/**
 * imagine building a tree from headings
 * [
 *  {title: 1, level: 1},
 *  {title: 2, level: 2},
 *  {title: 3, level: 3},
 *  {title: 4, level: 3},
 *  {title: 5, level: 2},
 *  {title: 6, level: 4}
 * ]
 *
 * 1
 * |-2
 *   |-3
 *   |-4
 * |-5
 *   |---6
 *
 */
const buildListItems = (root: TreeNodeWithHeading, startLevel: number, headings: HeadingItem[]) => {
  const nodePathToIdMap = new Map<string, string>();
  const parentNodesForEachLevel: Record<number, TreeNode> = [];
  parentNodesForEachLevel[startLevel] = root;
  const nodeIds: string[] = [root.id];
  const levelIndentation = 32;
  const defaultTreeIndentation = 32;
  const findParent = (level: number): { level: number; node: TreeNode } => {
    let parentLevel = level - 1;
    while (parentLevel >= 0) {
      if (parentNodesForEachLevel[parentLevel]) {
        return { level: parentLevel, node: parentNodesForEachLevel[parentLevel] };
      }
      // eslint-disable-next-line no-plusplus
      parentLevel--;
    }
    throw Error(`No parent found at level${level}`);
  };
  headings.forEach((heading) => {
    const { title, slug, level } = heading;
    const { level: parentLevel, node: parent } = findParent(level);
    const path = getMemberSlug(slug);
    const tree: TreeNodeWithHeading = {
      id: `${parent.id}${treeDelimiter}${path}`,
      value: title,
      path: `#${path}`,
      children: [],
      heading,
    };
    tree.props = {};
    const marginLeft = (level - parentLevel) * levelIndentation - defaultTreeIndentation;
    if (marginLeft !== 0) {
      tree.props.style = { marginLeft };
    }
    nodePathToIdMap.set(path, tree.id);
    parent.children?.push(tree);
    if (nodeIds.includes(tree.id)) {
      throwErrorOnServer(`Duplicate tree id: ${tree.id}`);
    }
    nodeIds.push(tree.id);
    parentNodesForEachLevel[level] = tree;
  });

  const expandedNodeIds: string[] = [];
  traverseTree(root, (t) => {
    if (t.children && t.children.length > 0) {
      expandedNodeIds.push(t.id);
    }
  });

  return {
    nodeIds,
    nodePathToIdMap,
    treeNode: root,
    expandedNodeIds,
  };
};

const AppMainConfigProvider: FunctionComponent<
  React.PropsWithChildren<AppMainConfigProviderProps>
> = ({ children, layoutType, headings = [], pageTitle }) => {
  const [selected, setSelected] = useState<string>('');
  const { setAccordionsExpanded } = useAppMainState();
  const { hideInPageNavigation, pageMaxWidth } = useAppConfig();
  const { currentElementId, setElementIds } = useElementsShowInView();
  const showContentTimer = useRef<number | undefined>(undefined);
  const showNavigationTimer = useRef<number | undefined>(undefined);
  const showRightNav = useMediaQuery((theme) => theme.breakpoints.up(rightNavMinBreakpoint));
  const mainMaxWidthIsPageMaxWidth = useMemo(
    () => [LayoutType.OpenCloudV2, LayoutType.Landing].includes(layoutType) || hideInPageNavigation,
    [hideInPageNavigation, layoutType],
  );

  const { treeNode, nodeIds, expandedNodeIds, nodePathToIdMap } = useMemo(() => {
    const minLevel = Math.min(...headings.map((heading) => heading.level));
    return buildListItems(
      {
        id: rootNodeId,
        value: '',
        path: null,
        children: [],
      },
      minLevel - 1,
      headings,
    );
  }, [headings]);

  const accordionNodeIds = useMemo<string[]>(() => {
    if (layoutType !== LayoutType.ApiReference) {
      return [];
    }
    return headings
      .filter((heading) => isInheritIteratorKey(heading.slug))
      .reduce((results, heading) => {
        const nodeId = nodePathToIdMap.get(getMemberSlug(heading.slug));
        if (nodeId) {
          results.push(nodeId);
        } else {
          throw Error(`unable to find heading slug from nodePathToIdMap ${heading.slug}`);
        }
        return results;
      }, [] as string[]);
  }, [headings, layoutType, nodePathToIdMap]);

  const expandableInfos = useMemo<ExpandableInfo[]>(() => {
    return accordionNodeIds.map((nodeId) => ({ nodeId, expanded: false }));
  }, [accordionNodeIds]);

  const initialExpanded = useMemo<string[]>(() => {
    const unexpandedNodeIds = new Set<string>(
      expandableInfos.filter((info) => !info.expanded).map((info) => info.nodeId),
    );
    return expandedNodeIds.filter((nodeId) => !unexpandedNodeIds.has(nodeId));
  }, [expandableInfos, expandedNodeIds]);

  const [expanded, _setExpanded] = useState<string[]>([]);

  const setExpanded = useCallback(
    (expandedIds: string[], forceExpanded = false) => {
      if (array.hasSameElements(expanded, expandedIds) && !forceExpanded) {
        return;
      }
      _setExpanded(expandedIds);
      if (layoutType === LayoutType.ApiReference) {
        const accordionIds = new Set(accordionNodeIds);
        setAccordionsExpanded(
          expandedIds.filter((nodeid) => accordionIds.has(nodeid)).map(getNodeIdByFullPath),
        );
      }
      setElementIds(setObervableElementIds(treeNode, expandedIds));
    },
    [accordionNodeIds, expanded, layoutType, setAccordionsExpanded, setElementIds, treeNode],
  );

  const setAccordionExpanded = useCallback(
    (accordionId: string, value: boolean) => {
      const accordionNodeId = nodePathToIdMap.get(accordionId);
      if (accordionNodeId) {
        _setExpanded((ids) => addOrRemoveStateElement(ids, accordionNodeId, value));
      }
    },
    [nodePathToIdMap],
  );

  const locationHash = typeof window !== 'undefined' ? window.location.hash.substring(1) : null;

  useEffect(() => {
    if (nodeIds.length < 2) {
      return;
    }
    // set default to the second node, since the first nodeId is the root
    const firstNode = nodeIds[1];
    let selectedId: string = firstNode;
    if (locationHash && nodePathToIdMap.get(locationHash)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      selectedId = nodePathToIdMap.get(locationHash)!;
      // handle hash element is in accordion
      window.clearInterval(showContentTimer.current);
      showContentTimer.current = showElementInViewById(locationHash);
    }
    setSelected(selectedId);
    // CRK-1603: showElementInViewById(firstNode) will cause an auto scroll to the first Header on page load
    if (selectedId !== firstNode && showRightNav) {
      window.clearInterval(showNavigationTimer.current);
      showNavigationTimer.current = showElementInViewById(getTreeItemElementId(selectedId));
    }
    const parentNodeId = getParentNodeId(selectedId);
    if (parentNodeId && parentNodeId !== rootNodeId) {
      const newNodes = getNodeIdsToExpand(parentNodeId, initialExpanded);
      setExpanded([...initialExpanded, ...newNodes], true);
    } else {
      setExpanded(initialExpanded, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headings]);

  useEffect(() => {
    if (currentElementId && showRightNav) {
      const nodeId = nodePathToIdMap.get(currentElementId);
      if (nodeId) {
        setSelected(nodeId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentElementId]);

  const rightNavIsEmpty = useMemo(() => treeNode.children.length === 0, [treeNode]);

  const mainMaxWidth = useMemo(() => {
    if (!rightNavIsEmpty) return appMain.articleMaxWidth;
    return mainMaxWidthIsPageMaxWidth ? pageMaxWidth : appMain.articleMaxWidth;
  }, [mainMaxWidthIsPageMaxWidth, rightNavIsEmpty, pageMaxWidth]);

  return (
    <AppMainConfigContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        rightNav: {
          treeNode,
          nodeIds,
          expandableInfos,
          expanded,
          setExpanded,
          isEmpty: rightNavIsEmpty,
          selected,
          setSelected,
        },
        mainMaxWidth,
        setAccordionExpanded,
        pageTitle,
      }}>
      {children}
    </AppMainConfigContext.Provider>
  );
};

export default AppMainConfigProvider;
